import { useMemo, useState } from "react";
import { useApp } from "../store";
import { est2kAnchorSeconds, fitTier, TIER_LABELS, type Tier } from "../lib/fitTier";
import { raceTimeToSeconds, secondsToRaceTime } from "../lib/format";

export default function SettingsPage() {
  const { settings, schools, putSettings } = useApp();
  const [a, setA] = useState(settings?.assumptions ?? null);

  const anchor = useMemo(() => est2kAnchorSeconds(schools), [schools]);

  // The tier thresholds are stored as cMax seconds-back; here we edit them as est-2k times.
  const [times, setTimes] = useState<TierTimes>(() => ({
    dream: secondsToRaceTime(anchor + (settings?.assumptions.tierThresholds.dream ?? 50), 0),
    stretch: secondsToRaceTime(anchor + (settings?.assumptions.tierThresholds.stretch ?? 55), 0),
    target: secondsToRaceTime(anchor + (settings?.assumptions.tierThresholds.target ?? 90), 0),
  }));

  if (!settings || !a) return null;

  const parsed = {
    dream: toSecsBack(times.dream, anchor),
    stretch: toSecsBack(times.stretch, anchor),
    target: toSecsBack(times.target, anchor),
  };
  const valid =
    parsed.dream != null &&
    parsed.stretch != null &&
    parsed.target != null &&
    parsed.dream < parsed.stretch &&
    parsed.stretch < parsed.target;

  const effThresholds = valid
    ? (parsed as { dream: number; stretch: number; target: number })
    : a.tierThresholds;

  function save() {
    if (!valid) return;
    void putSettings({
      assumptions: {
        ...a!,
        tierThresholds: parsed as { dream: number; stretch: number; target: number },
      },
    });
  }

  const counts: Record<Tier, number> = { dream: 0, stretch: 0, target: 0, floor: 0, unknown: 0 };
  for (const s of schools) counts[fitTier(s, effThresholds)]++;

  const setK = (k: keyof TierTimes, v: string) => setTimes({ ...times, [k]: v });

  return (
    <div className="max-w-2xl space-y-6">
      <section className="card">
        <h3 className="font-semibold">Fit tiers</h3>
        <p className="mt-1 text-sm text-slate-500">
          A school's tier comes from its <strong>estimated 1V 8+ 2k</strong> — a modeled crew speed
          from national results, where a faster time means a stronger, more competitive program.
          Edit the cut-off times right in the table; every school falls into one band.
        </p>

        <div className="mt-3 overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2 text-left font-semibold">Tier</th>
                <th className="px-3 py-2 text-left font-semibold">Est. 1V 8+ 2k range</th>
                <th className="px-3 py-2 text-right font-semibold">Schools</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="w-36 px-3 py-2 font-medium">{TIER_LABELS.dream}</td>
                <td className="px-3 py-2 text-slate-600">
                  faster than <TimeInput value={times.dream} onChange={(v) => setK("dream", v)} />
                </td>
                <td className="w-20 px-3 py-2 text-right tabular-nums text-slate-500">
                  {counts.dream}
                </td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-3 py-2 font-medium">{TIER_LABELS.stretch}</td>
                <td className="px-3 py-2 text-slate-600">
                  <Echo>{times.dream}</Echo> to{" "}
                  <TimeInput value={times.stretch} onChange={(v) => setK("stretch", v)} />
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                  {counts.stretch}
                </td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-3 py-2 font-medium">{TIER_LABELS.target}</td>
                <td className="px-3 py-2 text-slate-600">
                  <Echo>{times.stretch}</Echo> to{" "}
                  <TimeInput value={times.target} onChange={(v) => setK("target", v)} />
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                  {counts.target}
                </td>
              </tr>
              <tr className="border-b border-slate-100 last:border-0">
                <td className="px-3 py-2 font-medium">{TIER_LABELS.floor}</td>
                <td className="px-3 py-2 text-slate-600">
                  slower than <Echo>{times.target}</Echo>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">{counts.floor}</td>
              </tr>
              {counts.unknown > 0 && (
                <tr className="bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-500">{TIER_LABELS.unknown}</td>
                  <td className="px-3 py-2 text-slate-500">not enough race results to model</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                    {counts.unknown}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!valid && (
          <p className="mt-2 text-sm text-rose-600">
            Enter each cut-off as <code>m:ss</code>, each slower than the row above.
          </p>
        )}
        <button className="btn btn-primary mt-3" disabled={!valid} onClick={save}>
          Save fit tiers
        </button>
      </section>

      <section className="card">
        <h3 className="font-semibold">Katie's pace assumptions</h3>
        <p className="text-sm text-slate-500">
          Reference notes only — update as her erg and race times develop.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
          {(
            [
              ["ergPr", "2k erg PR"],
              ["ergProjected", "Projected 2k erg"],
              ["v2Time", "2V race time"],
              ["v2Projected", "Projected 2V time"],
            ] as const
          ).map(([k, label]) => (
            <label key={k} className="block">
              <span className="label">{label}</span>
              <input
                className="input w-full"
                value={a[k]}
                onChange={(e) => setA({ ...a, [k]: e.target.value })}
              />
            </label>
          ))}
        </div>
        <button
          className="btn btn-primary mt-3"
          onClick={() => void putSettings({ assumptions: { ...a } })}
        >
          Save assumptions
        </button>
      </section>

      <section className="card">
        <h3 className="font-semibold">Export</h3>
        <p className="text-sm text-slate-500">
          Download the full record (all schools + evaluations + contact logs).
        </p>
        <div className="mt-3 flex gap-2">
          <a className="btn" href="/api/export.csv">
            Download CSV
          </a>
          <a className="btn" href="/api/export.json">
            Download JSON
          </a>
        </div>
      </section>
    </div>
  );
}

type TierTimes = { dream: string; stretch: string; target: string };

function TimeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      className="input mx-0.5 inline-block w-20 tabular-nums"
      placeholder="m:ss"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function Echo({ children }: { children: React.ReactNode }) {
  return <span className="font-medium tabular-nums text-slate-700">{children}</span>;
}

/** "6:38" + anchor 347.7 -> 50.3 seconds-back. null if unparseable. */
function toSecsBack(time: string, anchor: number): number | null {
  const s = raceTimeToSeconds(time);
  if (s == null) return null;
  return Math.round((s - anchor) * 10) / 10;
}
